import { useEffect, useRef } from "react";

/**
 * Cursor-following spotlight — a soft brand-coloured circle of light that
 * tracks the mouse ONLY while it's inside the hero section.
 * Uses a fixed-position div with a radial gradient. Zero re-renders (raw DOM).
 * Automatically disabled on touch devices and when prefers-reduced-motion is on.
 */
export const HeroSpotlight = ({ containerRef }) => {
  const dotRef = useRef(null);

  useEffect(() => {
    const container = containerRef?.current;
    const dot = dotRef.current;
    if (!container || !dot) return;

    // Skip on touch-only devices + reduced-motion users
    if (window.matchMedia("(hover: none)").matches) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    let raf = 0;
    let tx = 0, ty = 0, x = 0, y = 0;
    let visible = false;

    const onMove = (e) => {
      const rect = container.getBoundingClientRect();
      tx = e.clientX;
      ty = e.clientY;
      if (
        tx >= rect.left && tx <= rect.right &&
        ty >= rect.top && ty <= rect.bottom
      ) {
        if (!visible) {
          dot.style.opacity = "1";
          visible = true;
        }
      } else if (visible) {
        dot.style.opacity = "0";
        visible = false;
      }
    };

    const onLeave = () => {
      dot.style.opacity = "0";
      visible = false;
    };

    const tick = () => {
      // ease-toward for a buttery-smooth follow (10% catch-up per frame)
      x += (tx - x) * 0.14;
      y += (ty - y) * 0.14;
      dot.style.transform = `translate3d(${x - 300}px, ${y - 300}px, 0)`;
      raf = requestAnimationFrame(tick);
    };

    window.addEventListener("pointermove", onMove, { passive: true });
    window.addEventListener("pointerleave", onLeave);
    raf = requestAnimationFrame(tick);

    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerleave", onLeave);
      cancelAnimationFrame(raf);
    };
  }, [containerRef]);

  return (
    <div
      ref={dotRef}
      aria-hidden="true"
      className="pointer-events-none fixed left-0 top-0 h-[600px] w-[600px] rounded-full opacity-0 transition-opacity duration-300 z-[1]"
      style={{
        background:
          "radial-gradient(circle at center, hsl(270 100% 60% / 0.18) 0%, hsl(320 100% 55% / 0.08) 30%, transparent 60%)",
        mixBlendMode: "screen",
      }}
      data-testid="hero-spotlight"
    />
  );
};

export default HeroSpotlight;
